import type {MixedOrSingleProperty, PropertySpecialState} from "@buildwithflux/core";
import type {
    IDocumentData,
    IElementData,
    INetsMap,
    IPropertiesMap,
    IPropertyData,
    IRouteData,
} from "@buildwithflux/models";
import {isEqual} from "lodash";
import {useShallow} from "zustand/react/shallow";

import {useFluxServices} from "../../../../injection/hooks";
import {select} from "../selectors";
import {
    computeCurrentSubject as computeCurrentSubjectFromSelectors,
    type ICurrentSubject,
} from "../selectors/subjects/derived";

// Re-export types from core and selectors for backward compatibility
export type {MixedOrSingleProperty, PropertySpecialState} from "@buildwithflux/core";
export type {ICurrentSubject} from "../selectors/subjects/derived";

// Re-export pure function (same signature) for backward compatibility
export {computeSelectedElementsCount} from "../selectors/subjects/derived";

/**
 * Backward-compatible wrapper for computeCurrentSubject.
 * Takes IDocumentData and delegates to the selector version which takes individual maps.
 * @param document - The document data
 * @param selectedSubjectsUids - Array of selected subject UIDs
 * @param isPCB - Whether we're in PCB mode (to check for PCB nodes)
 * @returns The current subject info
 */
export function computeCurrentSubject(
    document: IDocumentData | undefined,
    selectedSubjectsUids: string[],
    isPCB: boolean,
): ICurrentSubject {
    return computeCurrentSubjectFromSelectors(
        document?.uid,
        document?.elements,
        document?.nets,
        isPCB ? document?.pcbLayoutNodes : undefined,
        selectedSubjectsUids,
    );
}

/**
 * Pure function to merge properties from selected subjects (elements, nets, routes).
 * When multiple subjects are selected, properties are merged and marked as "mixed" if values differ.
 * Only properties that exist on ALL selected subjects are included in the result.
 *
 * NOTE: Unlike the canonical mergeSelectedSubjectProperties in selectors/subjects/derived.ts,
 * this version does NOT enrich net properties with computed Current/Max Current values.
 * This preserves backward compatibility for non-reactive callers that import from document.ts.
 *
 * @param document - The document data containing elements, nets, routes, and properties
 * @param selectedSubjectsUids - Array of selected subject UIDs
 * @returns Merged properties map, or document properties if no subjects are selected
 */
export function mergeSelectedSubjectProperties(
    document: IDocumentData | undefined,
    selectedSubjectsUids: string[],
): {[key: string]: MixedOrSingleProperty} | IPropertiesMap | undefined {
    const documentProperties = document?.properties;
    if (documentProperties && selectedSubjectsUids.length === 0) {
        return documentProperties;
    }

    const elements = document?.elements ?? {};
    const routes = document?.routes ?? {};
    const nets = document?.nets ?? {};

    const selectedElements: IElementData[] = [];
    const selectedRoutes: IRouteData[] = [];
    const selectedNets: INetsMap[string][] = [];

    for (const uid of selectedSubjectsUids) {
        if (elements[uid]) {
            selectedElements.push(elements[uid]!);
        }
        if (routes[uid]) {
            selectedRoutes.push(routes[uid]!);
        }
        if (nets[uid]) {
            selectedNets.push(nets[uid]!);
        }
    }

    const mergedProperties: {[key: string]: MixedOrSingleProperty} = {};
    let firstIteration = true;

    const mergeProperties = (subject: IElementData | INetsMap[string] | IRouteData): void => {
        if (!subject.properties) return;

        const subjectPropertyNames = new Set<string>();
        for (const property of Object.values(subject.properties)) {
            if (firstIteration) {
                mergedProperties[property.name] = property;
            } else if (mergedProperties[property.name]) {
                const existingProperty = mergedProperties[property.name]!;
                const {uid: _uid, ...propertyWithoutUid} = existingProperty as IPropertyData;
                const multiProperty: PropertySpecialState = {
                    ...propertyWithoutUid,
                    state: "state" in existingProperty ? (existingProperty as PropertySpecialState).state : "multiple",
                };
                if (!isEqual(multiProperty.value, property.value)) {
                    multiProperty.value = "";
                    multiProperty.state = "mixed";
                }
                mergedProperties[property.name] = multiProperty;
            }
            subjectPropertyNames.add(property.name);
        }

        for (const key of Object.keys(mergedProperties)) {
            if (!subjectPropertyNames.has(key)) {
                delete mergedProperties[key];
            }
        }

        firstIteration = false;
    };

    for (const element of selectedElements) {
        mergeProperties(element);
    }
    for (const net of selectedNets) {
        mergeProperties(net);
    }
    for (const route of selectedRoutes) {
        mergeProperties(route);
    }

    return mergedProperties;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to get the count of selected elements (excluding branch points) from the Zustand store
 * Delegates to the subjects.selectedElementsCount selector.
 * @param elementUids - Optional array of element UIDs to count (defaults to all selected)
 * @returns The count of selected elements
 */
export const useSelectedElementsCount = (elementUids?: string[]): number => {
    return useFluxServices().useDocumentStore(select.subjects.selectedElementsCount(elementUids));
};

/**
 * Hook to get the current subject based on selection
 * This determines what type of subject is currently selected (element, net, node, or document)
 * Delegates to the subjects.currentSubject selector.
 * NOTE: Uses shallow comparison to avoid unnecessary re-renders
 * @param selectedSubjectsUids - Array of selected subject UIDs
 * @param isPCB - Whether we're in PCB mode (to check for PCB nodes)
 * @returns The current subject info
 */
export const useCurrentSubject = (selectedSubjectsUids: string[], isPCB: boolean): ICurrentSubject => {
    return useFluxServices().useDocumentStore(useShallow(select.subjects.currentSubject(selectedSubjectsUids, isPCB)));
};

/**
 * Hook to get the merged properties of all selected subjects from the Zustand store
 * When multiple subjects are selected, properties are merged and marked as "mixed" if values differ
 * Delegates to the subjects.selectedSubjectProperties selector.
 * NOTE: Uses shallow comparison to avoid unnecessary re-renders
 * @param currentSubject - The current subject info from useCurrentSubject (unused but kept for API compatibility)
 * @param selectedSubjectsUids - Array of selected subject UIDs
 * @returns Merged properties map
 */
export const useSelectedSubjectProperties = (
    _currentSubject: ICurrentSubject,
    selectedSubjectsUids: string[],
): Record<string, MixedOrSingleProperty> | IPropertiesMap | undefined => {
    return useFluxServices().useDocumentStore(
        useShallow(select.subjects.selectedSubjectProperties(selectedSubjectsUids)),
    );
};
